import type {
  CreatorSummary,
  PublicFeaturedTag,
  PublicTag,
  PublicTagDetail,
  TagCatalogPage,
  TagCategory,
} from '@/types/api'
import { requestData } from './client'

/**
 * 作者与标签。openapi.yaml tags: public。
 *
 * 两个列表都**没有分页也没有排序**：只有 `q`、`limit`（默认 20、最大 100），
 * 标签额外多一个 `category`。标签本身是扁平的，没有父子层级 —— 站点的五栏
 * （类型 / 作者 / 游戏 / 其他 / 更新时间）由三个来源拼成：
 *
 *   - 类型 / 游戏 / 其他 ← `GET /tags?category=type|game|other`（后端字段，
 *     2026-08-08 起可用，见 docs/backend-sync/2026-08-08-backend-contract-update.md）；
 *   - 作者 ← `GET /creators`，它落到 `author_name` 条件而不是 `tag`：
 *     作者在后端是一等实体，不是标签；
 *   - 更新时间 ← Post 上的 `update_period`，也不是标签。
 *
 * `category` 传非法值返回 **422**，不是空列表 —— 所以不要把用户可编辑的字符串
 * 直接透传进来。
 */

interface ItemsEnvelope<T> {
  items: T[]
}

export async function fetchCreators(
  params: { q?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<CreatorSummary[]> {
  const data = await requestData<ItemsEnvelope<CreatorSummary>>('/creators', {
    query: { ...(params.q ? { q: params.q } : {}), ...(params.limit ? { limit: params.limit } : {}) },
    ...(signal ? { signal } : {}),
  })
  return data.items
}

export function fetchCreator(publicId: string, signal?: AbortSignal): Promise<CreatorSummary> {
  return requestData<CreatorSummary>(`/creators/${encodeURIComponent(publicId)}`, {
    ...(signal ? { signal } : {}),
  })
}

export interface FetchTagsParams {
  q?: string
  /** 只取某一栏。省略表示三栏都要。 */
  category?: TagCategory
  /** 默认 20，最大 100。 */
  limit?: number
}

export async function fetchTags(
  params: FetchTagsParams = {},
  signal?: AbortSignal,
): Promise<PublicTag[]> {
  const data = await requestData<ItemsEnvelope<PublicTag>>('/tags', {
    query: {
      ...(params.q ? { q: params.q } : {}),
      ...(params.category ? { category: params.category } : {}),
      ...(params.limit ? { limit: params.limit } : {}),
    },
    ...(signal ? { signal } : {}),
  })
  return data.items
}

/**
 * 单个标签（`GET /tags/{public_id}`）。
 *
 * 标签落地页 `/t/:publicId` 用它拿标题与作品数。**不要改成从 `fetchTags`
 * 里找**：那条路由最多返回 100 条，排在后面的标签会查不到。
 */
export function fetchTag(publicId: string, signal?: AbortSignal): Promise<PublicTagDetail> {
  return requestData<PublicTagDetail>(`/tags/${encodeURIComponent(publicId)}`, {
    ...(signal ? { signal } : {}),
  })
}

export interface FetchTagCatalogParams {
  /** 按标签名的**子串**过滤（后端不是相似度匹配，敲前缀就能命中）。 */
  q?: string
  category?: TagCategory
  /** 从 1 开始，上限 1000。 */
  page?: number
  /** 默认 20，最大 100。 */
  limit?: number
}

/**
 * 「标签大全」页的数据源（`GET /tags/catalog`）。
 *
 * 与 `fetchTags` 是两条不同的路由，不要互相顶替：
 *
 * | | `GET /tags` | `GET /tags/catalog` |
 * |---|---|---|
 * | 用途 | 选择器/筛选面板的候选值 | 「标签大全」浏览页 |
 * | 排序 | trigram 相似度 | **作品数递减** |
 * | 分页 | 无（最多 100 条） | `page` + `total` |
 * | 作品数 | 无 | 有 |
 * | 空标签 | 照常返回 | **不返回** |
 *
 * 最后一行是这条路由唯一会"少给"的东西：作品数为 0 的标签点进去必然是空列表，
 * 后端把它们从浏览入口里排除了，但它们在选择器里照常可选。
 */
export function fetchTagCatalog(
  params: FetchTagCatalogParams = {},
  signal?: AbortSignal,
): Promise<TagCatalogPage> {
  return requestData<TagCatalogPage>('/tags/catalog', {
    query: {
      ...(params.q ? { q: params.q } : {}),
      ...(params.category ? { category: params.category } : {}),
      ...(params.page && params.page > 1 ? { page: params.page } : {}),
      ...(params.limit ? { limit: params.limit } : {}),
    },
    ...(signal ? { signal } : {}),
  })
}

/**
 * 首页展示位（`GET /featured-tags`）。
 *
 * 与 `fetchTags` 是两回事，不要互相替代：这里返回的是**运营指定**的那几格
 * （带封面、带次序），而 `/tags` 是全站标签的字母序清单。后端为此专门建了一张
 * `featured_tags` 表，`tags` 上一列没加。
 *
 * **返回顺序就是展示顺序**（后端按 `sort_order, created_at, id` 排），
 * 响应里没有 `sort_order` 可供前端重排 —— 也不该重排。
 *
 * 只有 `limit`（通用的默认 20 / 上限 100），没有 cursor：这份配置的规模由首页
 * 放得下几格决定。
 */
export async function fetchFeaturedTags(
  params: { limit?: number } = {},
  signal?: AbortSignal,
): Promise<PublicFeaturedTag[]> {
  const data = await requestData<ItemsEnvelope<PublicFeaturedTag>>('/featured-tags', {
    query: { ...(params.limit ? { limit: params.limit } : {}) },
    ...(signal ? { signal } : {}),
  })
  return data.items
}
